import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { VAS_BANDS, vasBand } from "../vasScale";
import { formatShortDate, timeOnly } from "./dateHelpers";
import ChiefComplaintsService from "./ChiefComplaintsService";
import StatusOverTimeChart from "./StatusOverTimeChart";
import { CC_V2_REFRESH } from "./events";
import ModalPortal from "../common/ModalPortal";
import { CalendarIcon } from "../labInvestigations/labIcons";
import MetaTooltip from "../medicines/MetaTooltip";

/**
 * "View trend" modal — audit history + stats + correction / incorrect
 * flow. Each audit row carries a trailing flag column:
 *
 *   • grey flag       → live/untouched     → ReviewEntryDialog (2 CTAs)
 *   • red flag        → corrected          → CorrectionHistoryDialog
 *   • red flag +      → marked incorrect   → MarkedIncorrectDialog
 *     row strike-through
 *
 * From the review dialog:
 *   • "Yes, correct entry" → CorrectEntryDialog → save → SuccessDialog
 *   • "Mark as incorrect"  → wire straight to /incorrect → SuccessDialog
 *
 * All actions are audit-only unless the corrected row happens to be the
 * trailing "current" segment, in which case the backend also mirrors
 * the corrected values into Complaint.Current so the main table
 * reflects the change without a separate write.
 */

// Reported by — server enriches `reportedByName` at read time.
const reporterLabel = (row) => {
  if (row && row.reportedByName) return row.reportedByName;
  const actor = row && row.reportedBy;
  if (!actor) return "—";
  const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
  return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
};

const actorLabel = (actor, actorName) => {
  if (actorName) return actorName;
  if (!actor) return "—";
  const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
  return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
};

// Filled flag glyph — red when marked (either incorrect or corrected),
// grey otherwise. Same shape used by every review-flow section.
const FlagIcon = ({ marked }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill={marked ? "#ef4444" : "#98a2b3"}
    aria-hidden="true"
  >
    <path d="M14.4 6l-.4-2H5v17h2v-7h5.6l.4 2h7V6z" />
  </svg>
);
FlagIcon.propTypes = { marked: PropTypes.bool };

// Map an audit row to one of three states — drives colour + click behaviour.
const rowState = (row) => {
  if (row.markedIncorrect) return "incorrect";
  if (row.correction) return "corrected";
  return "live";
};

// "13 Jan 2026, 3:02 pm" — used in the correction-history popup header.
const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  return `${date}, ${time}`;
};

const formatTimeOnly = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
};

// ─── Review dialog ─────────────────────────────────────────────────────────
// Two CTAs — the correction path is primary (purple), mark-incorrect is a
// secondary destructive-outline button. Matches the mock's button ordering.
const ReviewEntryDialog = ({ submitting, onMarkIncorrect, onCorrect, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onClose}>
    <div
      className="alg-review cc-review-wide"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-review-head">
        <h3 className="alg-review-title">Review this entry</h3>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <p className="alg-review-body">
        You can correct this entry&rsquo;s details, or mark the whole entry
        as incorrect.
      </p>
      <p className="alg-review-body">
        <strong>Please note: Correction is allowed only once.</strong>
      </p>
      <footer className="alg-review-foot cc-review-foot">
        <button
          type="button"
          className="alg-review-mark"
          onClick={onMarkIncorrect}
          disabled={submitting}
        >
          Mark as incorrect
        </button>
        <button
          type="button"
          className="alg-success-done"
          onClick={onCorrect}
          disabled={submitting}
        >
          Yes, correct entry
        </button>
      </footer>
    </div>
  </div>
);
ReviewEntryDialog.propTypes = {
  submitting: PropTypes.bool,
  onMarkIncorrect: PropTypes.func.isRequired,
  onCorrect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Success dialog ────────────────────────────────────────────────────────
// One reusable card — the wording swaps by intent (`mode`).
const SuccessDialog = ({ mode, onDone }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onDone}>
    <div
      className="alg-success"
      onMouseDown={(e) => e.stopPropagation()}
      role="alertdialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="alg-review-close"
        onClick={onDone}
        aria-label="Close"
      >
        ×
      </button>
      <span className="alg-success-medallion" aria-hidden="true">
        <FlagIcon marked />
      </span>
      <h3 className="alg-success-title">
        {mode === "incorrect" ? "Entry marked as incorrect" : "Correction saved"}
      </h3>
      <p className="alg-success-body">
        {mode === "incorrect"
          ? "This entry is excluded from trends and graphs. It remains in the audit history."
          : "The audit entry now shows the corrected values. Its previous values are preserved in the correction history."}
      </p>
      <button type="button" className="alg-success-done" onClick={onDone}>
        Done
      </button>
    </div>
  </div>
);
SuccessDialog.propTypes = {
  mode: PropTypes.oneOf(["incorrect", "corrected"]).isRequired,
  onDone: PropTypes.func.isRequired,
};

// ─── Marked-incorrect audit dialog ─────────────────────────────────────────
const MarkedIncorrectDialog = ({ row, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
    <div
      className="alg-marked"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-marked-head">
        <span className="alg-marked-eyebrow">
          <FlagIcon marked /> MARKED INCORRECT
        </span>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <dl className="alg-marked-dl">
        <dt>By</dt>
        <dd>{actorLabel(row.markedIncorrectBy, row.markedIncorrectByName)}</dd>
        <dt>On</dt>
        <dd>{row.markedIncorrectAt ? formatShortDate(row.markedIncorrectAt) : "—"}</dd>
        <dt>At</dt>
        <dd>{row.markedIncorrectAt ? formatTimeOnly(row.markedIncorrectAt) : "—"}</dd>
      </dl>
    </div>
  </div>
);
MarkedIncorrectDialog.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Correction history dialog (Original vs Latest) ───────────────────────
const CorrectionHistoryDialog = ({ complaintName, row, onClose }) => {
  const prev = (row.correction && row.correction.previousSnapshot) || {};
  const correctedAt = row.correction && row.correction.at;
  const originalStatus = vasBand(prev.vas || 0).label;
  const latestStatus = vasBand(row.vas || 0).label;
  return (
    <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
      <div
        className="cc-correction-history"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="cc-corr-head">
          <div>
            <h3 className="cc-corr-title">Correction history</h3>
            <p className="cc-corr-sub">
              {complaintName}
              {correctedAt ? ` · corrected ${formatDateTime(correctedAt)}` : ""}
              {row.correction && (row.correction.byName || row.correction.by)
                ? ` · by ${actorLabel(row.correction.by, row.correction.byName)}`
                : ""}
            </p>
          </div>
          <button
            type="button"
            className="alg-review-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <section className="cc-corr-panel">
          <div className="cc-corr-panel-head">
            <span className="cc-corr-panel-label">ORIGINAL ENTRY</span>
            <span className="cc-corr-pill is-incorrect">Marked incorrect</span>
          </div>
          <dl className="cc-corr-dl">
            <dt>Since when</dt>
            <dd>{prev.sinceWhen ? formatShortDate(prev.sinceWhen) : "—"}</dd>
            <dt>To when</dt>
            <dd>{prev.toWhen ? formatShortDate(prev.toWhen) : "—"}</dd>
            <dt>Description</dt>
            <dd>{prev.description ? prev.description : "—"}</dd>
            <dt>Consulted doctor</dt>
            <dd>{prev.consultedDoctor ? "Yes" : "No"}</dd>
            <dt>Status</dt>
            <dd>{originalStatus}</dd>
          </dl>
        </section>

        <section className="cc-corr-panel is-latest">
          <div className="cc-corr-panel-head">
            <span className="cc-corr-panel-label">LATEST ENTRY</span>
            <span className="cc-corr-pill is-current">Current</span>
          </div>
          <dl className="cc-corr-dl">
            <dt>Since when</dt>
            <dd>{row.sinceWhen ? formatShortDate(row.sinceWhen) : "—"}</dd>
            <dt>To when</dt>
            <dd>{row.toWhen ? formatShortDate(row.toWhen) : "—"}</dd>
            <dt>Description</dt>
            <dd>{row.description ? row.description : "—"}</dd>
            <dt>Consulted doctor</dt>
            <dd>{row.consultedDoctor ? "Yes" : "No"}</dd>
            <dt>Status</dt>
            <dd>{latestStatus}</dd>
          </dl>
        </section>

        <footer className="cc-corr-foot">
          <button
            type="button"
            className="alg-success-done cc-corr-done"
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
};
CorrectionHistoryDialog.propTypes = {
  complaintName: PropTypes.string,
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Correction editor dialog ─────────────────────────────────────────────
// Bands drive the "Status · VAS scale" dropdown — the user picks a band,
// which we send as its representative score (the mid of the band's
// range). Backend classifiers stay VAS-numeric-native; this is only a
// UI convenience so the reviewer doesn't have to think about a 0-10
// integer.
const bandRepresentativeScore = (band) => {
  if (!band) return 0;
  return Math.round((band.min + band.max) / 2);
};
const bandKeyForVas = (vas) => vasBand(vas || 0).key;

/**
 * Inline VAS-band picker for the correction dialog. Renders the menu in
 * the same DOM subtree as the trigger (no ReactDOM portal), so the whole
 * click flow stays inside the `.cc-correct-entry` stopPropagation boundary
 * that the nested trend + correction backdrops rely on. Uses the same
 * visual language as the Add-Complaint Select — trigger pill with a
 * leading colour dot, drop-open menu with an is-selected highlight.
 */
const VasBandSelect = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const selected = VAS_BANDS.find((b) => b.key === value) || VAS_BANDS[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div
      className="cc-corr-vas-wrap"
      style={{ "--cc3-dot": selected.dot || selected.fg }}
      ref={boxRef}
    >
      <button
        type="button"
        className={`cc-corr-vas-trigger${open ? " is-open" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="cc-corr-vas-label">{selected.label}</span>
        <span className="cc-corr-vas-caret" aria-hidden="true">&#9662;</span>
      </button>
      {open && (
        <ul className="cc-corr-vas-menu" role="listbox">
          {VAS_BANDS.map((b) => (
            <li key={b.key}>
              <button
                type="button"
                role="option"
                aria-selected={b.key === value}
                className={`cc-corr-vas-option${b.key === value ? " is-selected" : ""}`}
                onClick={() => {
                  onChange(b.key);
                  setOpen(false);
                }}
              >
                {b.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
VasBandSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Word cap — matches the 250-word ceiling used across other v2 sections.
const MAX_DESCRIPTION_WORDS = 250;
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");
const toIsoUtc = (dateStr) => (dateStr ? new Date(dateStr).toISOString() : null);
const todayISO = () => new Date().toISOString().slice(0, 10);

const CorrectEntryDialog = ({ complaintName, row, submitting, onSubmit, onClose }) => {
  const [sinceWhen, setSinceWhen] = useState(toDateInput(row.sinceWhen));
  const [toWhen, setToWhen] = useState(toDateInput(row.toWhen));
  const [description, setDescription] = useState(row.description || "");
  const [bandKey, setBandKey] = useState(bandKeyForVas(row.vas));
  const [consultedDoctor, setConsultedDoctor] = useState(!!row.consultedDoctor);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const band = VAS_BANDS.find((b) => b.key === bandKey) || VAS_BANDS[0];

  // Dirty check — Save is a no-op unless something actually changed.
  const isDirty = useMemo(() => {
    if (sinceWhen !== toDateInput(row.sinceWhen)) return true;
    if (toWhen !== toDateInput(row.toWhen)) return true;
    if ((description || "").trim() !== (row.description || "").trim()) return true;
    if (bandKey !== bandKeyForVas(row.vas)) return true;
    if (consultedDoctor !== !!row.consultedDoctor) return true;
    return false;
  }, [row, sinceWhen, toWhen, description, bandKey, consultedDoctor]);

  const toWhenBeforeSince =
    toWhen && sinceWhen && new Date(toWhen).getTime() < new Date(sinceWhen).getTime();

  // Future dates aren't accepted for either boundary — the picker's `max`
  // already greys them out, but a manually-typed value can still land in
  // the future, so we double-guard by disabling Save.
  const todayStr = todayISO();
  const anyFuture =
    (sinceWhen && sinceWhen > todayStr) || (toWhen && toWhen > todayStr);

  const canSubmit =
    !submitting && !!sinceWhen && !toWhenBeforeSince && !anyFuture && isDirty;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      sinceWhen: toIsoUtc(sinceWhen),
      toWhen: toWhen ? toIsoUtc(toWhen) : null,
      description: description.trim(),
      vas: bandRepresentativeScore(band),
      consultedDoctor,
    });
  };

  return (
    <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onClose}>
      <div
        className="cc-correct-entry"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="cc-corr-head">
          <h3 className="cc-corr-title">
            Correct entry
            {complaintName && <span className="cc-corr-sub-inline"> · {complaintName}</span>}
          </h3>
          <button
            type="button"
            className="alg-review-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="cc-correct-body">
          <div className="cc-correct-row">
            <div className="sec-field">
              <label className="sec-label" htmlFor="cc-corr-since">
                Since when <span className="sec-label-required">*</span>
              </label>
              {/* Same date-input treatment as the medicine Add flow —
                  bordered pill with a trailing calendar icon so the two
                  surfaces share one visual language. */}
              <span className="med3-date">
                <input
                  id="cc-corr-since"
                  className="med3-date-input"
                  type="date"
                  value={sinceWhen}
                  onChange={(e) => setSinceWhen(e.target.value)}
                  max={todayStr}
                  disabled={submitting}
                />
                <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
              </span>
            </div>
            <div className="sec-field">
              <label className="sec-label" htmlFor="cc-corr-to">
                To when <span className="sec-label-required">*</span>
              </label>
              <span className={`med3-date${toWhenBeforeSince ? " is-error" : ""}`}>
                <input
                  id="cc-corr-to"
                  className="med3-date-input"
                  type="date"
                  value={toWhen}
                  onChange={(e) => setToWhen(e.target.value)}
                  max={todayStr}
                  disabled={submitting}
                />
                <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
              </span>
              {toWhenBeforeSince && (
                <p className="fam-field-error">To when cannot precede Since when.</p>
              )}
            </div>
          </div>

          {/* Status + Consulted doctor share a row above Description —
              both are compact controls, so pairing them saves vertical
              space and puts the classification decisions together before
              the free-text note. */}
          <div className="cc-correct-row">
            <div className="sec-field">
              <label className="sec-label" htmlFor="cc-corr-vas">
                Status <span className="sec-label-required">*</span>{" "}
                <span className="sec-label-note">· VAS scale</span>
              </label>
              {/* Inline VasBandSelect (custom dropdown) — the shared Select
                  component portals its menu to <body> and lost option
                  clicks inside the trend + correction nested-modal stack.
                  Rendering the menu in the same subtree keeps the click
                  flow inside .cc-correct-entry's stopPropagation boundary. */}
              <VasBandSelect
                value={bandKey}
                onChange={setBandKey}
                disabled={submitting}
              />
            </div>

            <div className="sec-field">
              <label className="sec-label">
                Consulted doctor? <span className="sec-label-required">*</span>
              </label>
              <div className="cc-corr-seg" role="radiogroup" aria-label="Consulted doctor">
                {[{ v: true, label: "Yes" }, { v: false, label: "No" }].map((o) => (
                  <button
                    key={o.label}
                    type="button"
                    role="radio"
                    aria-checked={consultedDoctor === o.v}
                    className={`cc-corr-seg-cell${consultedDoctor === o.v ? " is-on" : ""}`}
                    onClick={() => setConsultedDoctor(o.v)}
                    disabled={submitting}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="sec-field">
            <label className="sec-label" htmlFor="cc-corr-desc">
              Description <span className="sec-label-note">optional</span>
              <span className="dx-word-hint">
                {countWords(description)} / {MAX_DESCRIPTION_WORDS} words
              </span>
            </label>
            <textarea
              id="cc-corr-desc"
              className="sec-textarea"
              rows={3}
              value={description}
              onChange={(e) =>
                setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))
              }
              disabled={submitting}
            />
          </div>
        </div>

        <footer className="cc-corr-foot">
          <button
            type="button"
            className="sec-btn sec-btn-ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="alg-success-done cc-corr-save"
            onClick={submit}
            disabled={!canSubmit}
            title={!isDirty ? "No changes to save" : ""}
          >
            {submitting ? "Saving…" : "Save correction"}
          </button>
        </footer>
      </div>
    </div>
  );
};
CorrectEntryDialog.propTypes = {
  complaintName: PropTypes.string,
  row: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Main modal ───────────────────────────────────────────────────────────
const TrendModal = ({ patientId, complaint, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null); // { row, index } — populated on flag click
  const [dialogMode, setDialogMode] = useState(null); // "review" | "correct" | "history" | "incorrect" | null
  const [successMode, setSuccessMode] = useState(null); // "incorrect" | "corrected" | null
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    const svc = new ChiefComplaintsService();
    svc.trend(
      patientId,
      complaint.complaintId,
      (res) => setData(res),
      (err) => setError((err && err.message) || "Failed to load trend")
    );
  }, [patientId, complaint.complaintId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const currentBand = data && vasBand(data.latestSeverity || 0);
  const audit = (data && data.audit) || [];
  // Table renders newest → oldest by `sinceWhen` so the reviewer sees
  // the most-recent change first regardless of the order the server
  // shipped rows in. `audit` stays in append order (index N-1 = live)
  // because the correct / mark-incorrect API is indexed against that
  // original position; we sort a display view and carry the original
  // index in each row for backend calls. Missing / unparseable dates
  // sort to the bottom.
  const displayAudit = audit
    .map((row, originalIdx) => {
      const t = row && row.sinceWhen ? new Date(row.sinceWhen).getTime() : NaN;
      return { row, originalIdx, _t: isNaN(t) ? -Infinity : t };
    })
    .sort((a, b) => b._t - a._t);
  // Trend chart uses the corrected picture only — entries flagged
  // incorrect are still shown in the audit table (with strike-through)
  // but must not steer the graph. Delete events are already stripped
  // inside StatusOverTimeChart.
  const chartAudit = audit.filter((e) => !e.markedIncorrect);

  // Version pulled from the doc's list refresh — the trend response
  // doesn't expose the doc version, so we pull it from the caller's
  // in-memory record via `complaint.__version` if present, else assume
  // whatever is on data. Safest fallback: 0 (server's shadow-doc guard
  // will 409 if a real doc exists — the client will refetch).
  const expectedVersion = (data && data.version) || (complaint && complaint.__version) || 0;

  const openReview = (row, index) => {
    setFlagTarget({ row, index });
    if (row.markedIncorrect) {
      setDialogMode("incorrect");
    } else if (row.correction) {
      setDialogMode("history");
    } else {
      setDialogMode("review");
    }
  };

  const closeAll = () => {
    setDialogMode(null);
    setFlagTarget(null);
    setSuccessMode(null);
  };

  const afterMutation = (mode) => {
    setSubmitting(false);
    setDialogMode(null);
    setSuccessMode(mode);
    EventService.emit(CC_V2_REFRESH);
    load(); // refresh the audit inside the modal too
  };

  const submitMarkIncorrect = () => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    const svc = new ChiefComplaintsService();
    svc.markHistoryIncorrect(
      patientId,
      complaint.complaintId,
      flagTarget.index,
      expectedVersion,
      () => afterMutation("incorrect"),
      (err) => {
        setSubmitting(false);
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Mark failed — please retry");
      }
    );
  };

  const submitCorrection = (body) => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    const svc = new ChiefComplaintsService();
    svc.correctHistory(
      patientId,
      complaint.complaintId,
      flagTarget.index,
      { ...body, expectedVersion },
      () => afterMutation("corrected"),
      (err) => {
        setSubmitting(false);
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Correction failed — please retry");
      }
    );
  };

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={submitting ? null : onClose}>
        <div
          className="cc-modal cc-modal-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">{complaint.complaint}</span>
              {currentBand && (
                <span
                  className="cc-modal-tag"
                  style={{ color: currentBand.fg, background: currentBand.bg }}
                >
                  {data.ongoing ? "Ongoing" : "Resolved"} · {currentBand.label}
                </span>
              )}
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>
  
          {!data && !error && <div className="cc-modal-body cc-modal-loading">Loading…</div>}
          {error && <div className="cc-modal-body cc-modal-error">{error}</div>}
  
          {data && (
            <div className="cc-modal-body">
              <div className="cc-trend-stats">
                <div>
                  <span className="cc-trend-lbl">Episodes</span>
                  <span className="cc-trend-val">{data.episodes}</span>
                </div>
                <div>
                  <span className="cc-trend-lbl">First</span>
                  <span className="cc-trend-val">{formatShortDate(data.first)}</span>
                </div>
                <div>
                  <span className="cc-trend-lbl">Latest</span>
                  <span className="cc-trend-val">{formatShortDate(data.latest)}</span>
                </div>
              </div>
  
              <div className="cc-trend-chart-wrap">
                <div className="cc-trend-chart-head">
                  <span className="cc-trend-chart-title">Status over time</span>
                  <span className="cc-trend-chart-range">
                    {formatShortDate(data.first)} – {formatShortDate(data.latest)}
                  </span>
                </div>
                <StatusOverTimeChart audit={chartAudit} />
                {currentBand && (
                  <p className="cc-trend-chart-latest">
                    Latest reported severity:{" "}
                    <strong style={{ color: currentBand.fg }}>{currentBand.label}</strong>
                    {" · "}
                    {formatShortDate(data.latestSeverityAt)}
                  </p>
                )}
              </div>
  
              <div className="cc-trend-audit">
                <div className="cc-trend-audit-head">
                  <span>Audit</span>
                  <span className="cc-trend-audit-count">
                    · {audit.length} entries
                  </span>
                </div>
                <div className="sec-table cc-trend-grid cc-trend-grid-flag" role="table">
                  <div className="sec-thead" role="row">
                    <span role="columnheader">Since when</span>
                    <span role="columnheader">Description</span>
                    <span role="columnheader">To when</span>
                    <span role="columnheader">Reported by</span>
                    <span role="columnheader">Reported on</span>
                    <span
                      role="columnheader"
                      className="cc-trend-th-tip"
                      data-tip="Consulted doctor"
                      aria-label="Consulted doctor"
                    >
                      Consulted doctor
                    </span>
                    <span role="columnheader">Status</span>
                    <span role="columnheader" aria-label="Actions" />
                  </div>
                  {displayAudit.map(({ row, originalIdx }) => {
                    const state = rowState(row);
                    const band = vasBand(row.vas || 0);
                    const isDelete = row.event === "delete";
                    const isResolved = row.event === "status" && row.vas === 0;
                    const flagMarked = state !== "live";
                    // Live row = highest original index (server append order).
                    // Under the new descending view it sits at the top; the
                    // flag stays hidden on it since it hasn't been superseded.
                    const isLive = originalIdx === audit.length - 1;
                    return (
                      <div
                        className={`sec-row${state === "incorrect" ? " is-marked" : ""}`}
                        role="row"
                        key={originalIdx}
                      >
                        <span className="sec-cell" role="cell">
                          {formatShortDate(row.sinceWhen)}
                        </span>
                        <span className="sec-cell" role="cell">
                          {/* 3-line clamp + white hover tooltip; only
                              reveals when the text is actually being
                              truncated. */}
                          <MetaTooltip text={row.description || ""} lines={3} />
                        </span>
                        <span className="sec-cell" role="cell">
                          {row.toWhen ? formatShortDate(row.toWhen) : "—"}
                        </span>
                        <span className="sec-cell" role="cell">
                          {reporterLabel(row)}
                        </span>
                        <span className="sec-cell med-trend-ts" role="cell">
                          <span className="med-trend-ts-date">
                            {formatShortDate(row.recordedAt)}
                          </span>
                          <span className="med-trend-ts-time">{timeOnly(row.recordedAt)}</span>
                        </span>
                        <span className="sec-cell" role="cell">
                          {row.consultedDoctor ? "Yes" : "No"}
                        </span>
                        <span className="sec-cell" role="cell">
                          {isDelete ? (
                            <span className="cc-audit-deleted">Deleted</span>
                          ) : isResolved ? (
                            <span className="cc-resolved">Resolved</span>
                          ) : (
                            <span
                              className="cc-trend-band"
                              style={{ background: band.bg, color: band.fg }}
                            >
                              {band.label}
                            </span>
                          )}
                        </span>
                        <span className="sec-cell cc-trend-flag-cell" role="cell">
                          {/* Skip the flag/icon on the live row — it
                              hasn't been superseded, so there is nothing
                              to correct or mark incorrect on it. The
                              backend expects the original append-order
                              index for correction / mark-incorrect calls,
                              so we pass `originalIdx` regardless of the
                              new descending display order. */}
                          {!isLive && (
                            <button
                              type="button"
                              className="cc-trend-flag"
                              onClick={() => openReview(row, originalIdx)}
                              aria-label={
                                state === "incorrect"
                                  ? "View incorrect-mark audit"
                                  : state === "corrected"
                                  ? "View correction history"
                                  : "Review this entry"
                              }
                              title={
                                state === "incorrect"
                                  ? "View incorrect-mark audit"
                                  : state === "corrected"
                                  ? "View correction history"
                                  : "Review this entry"
                              }
                            >
                              <FlagIcon marked={flagMarked} />
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
  
        {/* Overlay dialogs — mounted separately so they stack above the trend
            modal via the shared .cc-modal-backdrop / .delc-backdrop pattern. */}
        {dialogMode === "review" && flagTarget && (
          <ReviewEntryDialog
            submitting={submitting}
            onMarkIncorrect={submitMarkIncorrect}
            onCorrect={() => setDialogMode("correct")}
            onClose={closeAll}
          />
        )}
        {dialogMode === "correct" && flagTarget && (
          <CorrectEntryDialog
            complaintName={complaint.complaint}
            row={flagTarget.row}
            submitting={submitting}
            onSubmit={submitCorrection}
            onClose={closeAll}
          />
        )}
        {dialogMode === "history" && flagTarget && (
          <CorrectionHistoryDialog
            complaintName={complaint.complaint}
            row={flagTarget.row}
            onClose={closeAll}
          />
        )}
        {dialogMode === "incorrect" && flagTarget && (
          <MarkedIncorrectDialog row={flagTarget.row} onClose={closeAll} />
        )}
        {successMode && <SuccessDialog mode={successMode} onDone={closeAll} />}
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  complaint: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
